import { useQuery } from '@apollo/client';
import { faGear } from '@fortawesome/pro-regular-svg-icons';
import { default as React, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import Button from '../../core/components/Button.tsx';
import ComboboxField from '../../core/components/Fields/ComboboxField.tsx';
import StringField from '../../core/components/Fields/StringField.tsx';
import FormField from '../../core/components/FormField.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import SaveStatus from '../../core/components/SaveStatus.tsx';
import Secret from '../../core/components/Secret.tsx';
import SettingsFormCard from '../../core/components/SettingsFormCard.tsx';
import { useToaster } from '../../core/components/Toast.tsx';
import Tooltip from '../../core/components/Tooltip.tsx';
import useSectionSaveStatus from '../../core/utils/hooks/useSectionSaveStatus.ts';
import { url } from '../../core/utils/link.ts';
import { CONFIG_FORM_FIELDS, CONFIG_FORM_SECTIONS, MASTER_DATA_DETAIL_PATH } from '../../masterdata/constants.ts';
import { GET_LOOKUP_DEFINITIONS } from '../../masterdata/queries.ts';
import { canManageMasterData } from '../../users/utils.ts';

const ConfigIcon = withIcon(faGear);

const FORM_DEFAULT_VALUES = {
    authToken: '',
    ...Object.fromEntries(CONFIG_FORM_FIELDS.map(({ name }) => [name, ''])),
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

interface LookupDefinition {
    id: string;
    name: string | null;
    lookupType: string;
}

export const LookupDefinitionSelectField = ({
    field,
    lookupType,
    disabled = false,
}: {
    field: any;
    lookupType?: string;
    disabled?: boolean;
}) => {
    const { t } = useTranslation('config');
    // Apollo deduplicates and caches this query, so each field instance shares one request
    const { data, loading, error } = useQuery(GET_LOOKUP_DEFINITIONS);

    // Fall back to manual UUID entry when the lookup definitions cannot be fetched
    // (e.g. the metadata auth token is not configured yet)
    if (error) return <StringField {...field} disabled={disabled} />;

    const definitions: LookupDefinition[] = data?.lookupDefinitions || [];
    const options = definitions
        .filter((definition) => !lookupType || definition.lookupType === lookupType)
        .map((definition) => ({ value: definition.id, label: definition.name || definition.id }))
        .sort((a, b) => a.label.localeCompare(b.label));

    // Keep a configured value visible even if it is not part of the fetched definitions
    if (field.value && !options.some((option) => option.value === field.value)) {
        options.unshift({ value: field.value, label: field.value });
    }

    return (
        <ComboboxField
            value={field.value || null}
            onValueChange={field.onChange}
            onBlur={field.onBlur}
            options={options}
            isLoading={loading}
            disabled={disabled}
            placeholder={t('configPage.masterData.form.selectPlaceholder')}
        />
    );
};

const LookupField = ({ name, form, user, lookupType }: any) => {
    const { t } = useTranslation('config');

    const lookupDefinitionId = form.watch(name);
    const error = form.formState.errors[name];

    return (
        <div className="flex gap-2 items-start">
            <Controller
                name={name}
                control={form.control}
                rules={{
                    validate: (value) =>
                        !value || UUID_PATTERN.test(value) || t('configPage.masterData.form.invalidUuid'),
                }}
                render={({ field }) => (
                    <FormField
                        label={t(`configPage.masterData.form.${name}`)}
                        error={error}
                        helpText={lookupDefinitionId && <Secret value={lookupDefinitionId} className="text-xs" />}
                        className="flex-1"
                    >
                        <LookupDefinitionSelectField field={field} lookupType={lookupType} />
                    </FormField>
                )}
            />

            {canManageMasterData(user) && (
                <div className="pt-6">
                    <Tooltip content={t('configPage.masterData.configureImport')}>
                        {lookupDefinitionId ? (
                            <Button variant="ghost" className="px-2" asChild>
                                <Link
                                    to={url(MASTER_DATA_DETAIL_PATH, { lookupDefinitionId })}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <ConfigIcon />
                                </Link>
                            </Button>
                        ) : (
                            <Button type="button" variant="ghost" className="px-2" disabled>
                                <ConfigIcon />
                            </Button>
                        )}
                    </Tooltip>
                </div>
            )}
        </div>
    );
};

const MasterDataConfig = ({ config, onSubmit, user }: any) => {
    const { t } = useTranslation('config');
    const { publishToast } = useToaster();

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    useEffect(() => {
        form.reset({
            authToken: config?.authToken || '',
            ...Object.fromEntries(CONFIG_FORM_FIELDS.map(({ name }) => [name, config?.[name] || ''])),
        });
    }, [config]);

    const handleSubmit = async (formData) => {
        try {
            await onSubmit(formData);
            publishToast({ description: t('configPage.masterData.updateSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('configPage.masterData.updateError'), status: 'error' });
        }
    };

    const { submitSection, getSaveStatusProps } = useSectionSaveStatus(form, handleSubmit);

    return (
        <div className="flex flex-col gap-10">
            <SettingsFormCard onSubmit={submitSection('authToken')} title={t('configPage.masterData.title')}>
                <Controller
                    name="authToken"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('configPage.masterData.form.authToken')} error={formErrors.authToken}>
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <SaveStatus {...getSaveStatusProps('authToken', ['authToken'])} />
            </SettingsFormCard>

            {CONFIG_FORM_SECTIONS.map(({ titleKey, fields }) => (
                <SettingsFormCard
                    key={titleKey}
                    onSubmit={submitSection(titleKey)}
                    title={t(`configPage.masterData.${titleKey}`)}
                >
                    {fields.map(({ name, lookupType }) => (
                        <LookupField key={name} name={name} form={form} user={user} lookupType={lookupType} />
                    ))}

                    <SaveStatus
                        {...getSaveStatusProps(
                            titleKey,
                            fields.map(({ name }) => name)
                        )}
                    />
                </SettingsFormCard>
            ))}
        </div>
    );
};

export default MasterDataConfig;
